<template>
  <section class="hero-new-block">
    <div class="hero-new-block__inner">
      <div class="hero-new-block__content">
        <div class="hero-new-block__text">
          <h1 v-if="title" class="paid-hero__title">{{ title }}</h1>
          <p v-if="subtitle" class="paid-hero__lead" v-html="subtitle"></p>
        </div>

        <div class="hero-new-block__search paid-search">
          <img src="@/assets/svg/search.svg" alt="" class="paid-search__icon" />
          <input
            v-model="searchQuery"
            type="search"
            class="paid-search__input"
            :placeholder="searchPlaceholder"
            :aria-label="searchPlaceholder"
            @input="onSearchInput"
          />

          <div v-if="searchQuery.trim() && results.length" class="hero-new-block__results search-results search-results--scrollable">
            <ul class="hero-new-block__results-list">
              <li v-for="result in results" :key="result.key" class="result-item">
                <router-link :to="result.path" class="result-link" @click.native="clearSearch">
                  {{ result.meta.title }}
                  <p v-if="result.meta.description" class="result-description">{{ result.meta.description }}</p>
                </router-link>
              </li>
            </ul>
          </div>
          <p v-else-if="searchQuery.trim() && !results.length" class="hero-new-block__empty no-results">
            Ничего не найдено по запросу «{{ searchQuery.trim() }}»
          </p>
        </div>
      </div>

      <div v-if="imageUrl" class="hero-new-block__image">
        <img :src="imageUrl" alt="" class="hero-new-block__image-img" />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { performSiteSearch } from '@/utils/siteSearch';
import { actionTypesPaid } from '@/store/modules/paidServices';
import { actionTypesNews } from '@/store/modules/news';
import { actionTypesDepartment } from '@/store/modules/department';
import { actionTypes as actionTypesPages } from '@/store/modules/pages';

export default {
  name: 'AppHeroNewBlock',
  inject: {
    searchState: { default: null },
  },
  props: {
    data: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      localQuery: '',
      results: [],
    };
  },
  computed: {
    ...mapState({
      paidServices: (state) => state.paidServices.paid,
      news: (state) => state.news.news,
      employees: (state) => state.department.EMPLOYEES,
      managements: (state) => state.department.MANAGEMENTS,
      chiefMedicalOfficer: (state) => state.department.CHIEF_MEDICAL_OFFICER,
      publishedPages: (state) => state.pages.menuItems,
    }),
    title() {
      return (this.data && this.data.title) || '';
    },
    subtitle() {
      return (this.data && this.data.subtitle) || '';
    },
    imageUrl() {
      const img = this.data && this.data.image;
      return img != null && String(img).trim() ? String(img).trim() : '';
    },
    searchPlaceholder() {
      const p = this.data && this.data.searchPlaceholder;
      return p != null && String(p).trim() ? String(p).trim() : 'Введите для поиска...';
    },
    searchQuery: {
      get() {
        if (this.searchState && typeof this.searchState.query === 'string') {
          return this.searchState.query;
        }
        return this.localQuery;
      },
      set(val) {
        const next = String(val || '');
        if (this.searchState) {
          this.searchState.query = next;
        } else {
          this.localQuery = next;
        }
      },
    },
  },
  watch: {
    searchQuery: {
      handler(val) {
        this.runSearch(val);
      },
    },
    paidServices: { handler() { this.runSearch(this.searchQuery); }, deep: true },
    publishedPages: { handler() { this.runSearch(this.searchQuery); }, deep: true },
    news: { handler() { this.runSearch(this.searchQuery); }, deep: true },
    employees: { handler() { this.runSearch(this.searchQuery); }, deep: true },
    managements: { handler() { this.runSearch(this.searchQuery); }, deep: true },
    chiefMedicalOfficer: { handler() { this.runSearch(this.searchQuery); }, deep: true },
  },
  mounted() {
    this.ensureSearchData();
    this.runSearch(this.searchQuery);
  },
  methods: {
    ensureSearchData() {
      if (!this.paidServices || !this.paidServices.length) {
        this.$store.dispatch(actionTypesPaid.getPaid).catch(() => {});
      }
      if (!this.news || !this.news.length) {
        this.$store.dispatch(actionTypesNews.getNews).catch(() => {});
      }
      if (
        (!this.employees || !this.employees.length)
        && (!this.managements || !this.managements.length)
        && (!this.chiefMedicalOfficer || !this.chiefMedicalOfficer.length)
      ) {
        this.$store.dispatch(actionTypesDepartment.getAllDoctors).catch(() => {});
      }
      if (!this.publishedPages || !this.publishedPages.length) {
        this.$store.dispatch(`pages/${actionTypesPages.getPages}`).catch(() => {});
      }
    },
    onSearchInput() {
      this.runSearch(this.searchQuery);
    },
    runSearch(query) {
      this.results = performSiteSearch(query, {
        paidServices: this.paidServices,
        publishedPages: this.publishedPages,
        news: this.news,
        employees: this.employees,
        managements: this.managements,
        chiefMedicalOfficer: this.chiefMedicalOfficer,
      });
    },
    clearSearch() {
      this.searchQuery = '';
      this.results = [];
    },
  },
};
</script>

<style scoped lang="scss">
.hero-new-block {
  width: 100%;
  margin: 12px 0 28px;
}

.hero-new-block__inner {
  display: flex;
  align-items: center;
  gap: 40px;
}

.hero-new-block__content {
  flex: 1;
  min-width: 0;
}

.hero-new-block__text {
  margin-bottom: 20px;
}

.hero-new-block__text .paid-hero__title {
  color: #2d5a27;
}

.hero-new-block__text:only-child {
  margin-bottom: 0;
}

.hero-new-block__search {
  position: relative;
}

.hero-new-block__image {
  flex-shrink: 0;
  max-width: 400px;
  width: 40%;
}

.hero-new-block__image-img {
  display: block;
  width: 100%;
  height: 200px;
  object-fit: contain;
  border-radius: 12px;
}

.hero-new-block__results {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  z-index: 20;
  margin-top: 0;
  text-align: left;
}

.hero-new-block__results-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.hero-new-block__empty {
  margin: 12px 0 0;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #e4e4e4;
  border-radius: 12px;
  font-family: "Circe", sans-serif;
  font-size: 15px;
  color: #888;
  text-align: center;
}

.paid-hero__lead :deep(a) {
  color: #66cc66;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
}

@media (max-width: 768px) {
  .hero-new-block__inner {
    flex-direction: column;
    gap: 24px;
  }

  .hero-new-block__image {
    max-width: 100%;
    width: 100%;
  }
}
</style>
